/**
 * CoS Product Catalog Section Custom Element
 * Handles carousel functionality and tab switching for product catalog section
 * Uses Splide slider and extends HTMLElement for better encapsulation
 */

class CosProductCatalogSection extends HTMLElement {
  constructor() {
    super();
    this.splides = new Map(); // Store multiple splide instances for tabs
    this.currentTab = null;
  }

  async connectedCallback() {
    // Wait for Splide to be available
    await ResourceCoordinator.requestVendor("Splide");

    // Initialize the component
    this.init();
  }

  disconnectedCallback() {
    // Cleanup when element is removed
    this.splides.forEach((splide) => {
      if (splide) {
        splide.destroy();
      }
    });
    this.splides.clear();
  }

  async init() {
    // Check if tabs are enabled
    const tabsEnabled = this.querySelector(".cos-tab-link");

    if (tabsEnabled) {
      // Initialize tabs functionality
      this.initializeTabs();
    } else {
      // Initialize regular splide
      await this.initializeSplide();
    }

    // Setup component functionality
    this.bindEvents();
    this.setupAccessibility();
  }

  initializeTabs() {
    const tabLinks = this.querySelectorAll(".cos-tab-link");
    const tabContents = this.querySelectorAll(".cos-tab-content");

    // Set first tab as active
    if (tabLinks.length > 0) {
      const firstTab = tabLinks[0].dataset.tab;
      this.currentTab = firstTab;
      this.initializeTabSplide(firstTab);
    }

    // Add click listeners to tab links
    tabLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        this.switchTab(link.dataset.tab);
      });
    });
  }

  switchTab(tabName) {
    if (tabName === this.currentTab) return;

    // Update active tab link
    this.querySelectorAll(".cos-tab-link").forEach((link) => {
      if (link.dataset.tab === tabName) {
        link.classList.add("border-b-2");
        link.classList.add("font-semibold");
      } else {
        link.classList.remove("border-b-2");
        link.classList.remove("font-semibold");
      }
    });

    // Show/hide tab content
    this.querySelectorAll(".cos-tab-content").forEach((content) => {
      if (content.dataset.tab === tabName) {
        content.classList.remove("hidden");
        content.classList.add("block");
      } else {
        content.classList.add("hidden");
        content.classList.remove("block");
      }
    });

    // Initialize splide for new tab if needed
    if (!this.splides.has(tabName)) {
      this.initializeTabSplide(tabName);
    }

    this.currentTab = tabName;
  }

  async initializeTabSplide(tabName) {
    const splideElement = this.querySelector(
      `.cos-product-catalog-splide-${tabName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "-")}`
    );

    // Only initialize Splide if we're on mobile/tablet (screen width < 768px)
    if (splideElement && window.innerWidth < 768) {
      const splide = new Splide(splideElement, {
        type: "slide",
        perPage: 1,
        perMove: 1,
        gap: "16px",
        autoWidth: true,
        arrows: false,
        pagination: true,
        breakpoints: {
          639: {
            perPage: 1,
            perMove: 1,
            arrows: false,
            gap: "8px",
            autoWidth: true,
          },
        },
      }).mount();

      this.splides.set(tabName, splide);
      console.log(`CoS Product Catalog Splide initialized for tab: ${tabName}`);
    }
  }

  async initializeSplide() {
    const splideElement = this.querySelector(".cos-product-catalog-splide");

    // Only initialize Splide if we're on mobile/tablet (screen width < 768px)
    if (splideElement && window.innerWidth < 768) {
      this.splides.set(
        "default",
        new Splide(splideElement, {
          type: "slide",
          perPage: 1,
          perMove: 1,
          gap: "16px",
          autoWidth: true,
          arrows: false,
          pagination: true,
          breakpoints: {
            639: {
              perPage: 1,
              perMove: 1,
              arrows: false,
              gap: "8px",
              autoWidth: true,
            },
          },
        }).mount()
      );

      console.log("CoS Product Catalog Splide initialized for regular layout");
    }

    // Remove placeholders based on screen size
    this.removePlaceholders();

    // Setup resize handler to manage responsive behavior
    this.setupResizeHandler();
  }

  removePlaceholders() {
    const mobilePlaceholder = this.querySelector(".splide-placeholder");
    const gridPlaceholder = this.querySelector(".grid-placeholder");
    mobilePlaceholder.remove();
    gridPlaceholder.remove();
  }

  setupResizeHandler() {
    let resizeTimer;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 150);
    });
  }

  handleResize() {
    const isMobile = window.innerWidth < 768;

    if (isMobile && this.splides.size === 0) {
      // Initialize splide for mobile if not already initialized
      const tabsEnabled = this.querySelector(".cos-tab-link");
      if (tabsEnabled) {
        // Reinitialize current tab splide
        if (this.currentTab) {
          this.initializeTabSplide(this.currentTab);
        }
      } else {
        // Reinitialize regular splide
        this.initializeSplide();
      }
    } else if (!isMobile && this.splides.size > 0) {
      // Destroy all splides for desktop
      this.splides.forEach((splide) => {
        if (splide) {
          splide.destroy();
        }
      });
      this.splides.clear();
    }
  }

  bindEvents() {
    // Handle general card clicks for analytics/tracking
    this.addEventListener("click", (e) => {
      const generalCard = e.target.closest('[data-block-type="general_card"]');
      if (generalCard) {
        this.handleGeneralCardClick(generalCard);
      }

      // Handle product card clicks for analytics/tracking
      const productCard = e.target.closest('[href*="/products/"]');
      if (productCard) {
        this.handleProductCardClick(productCard);
      }
    });
  }

  setupAccessibility() {
    // Add keyboard navigation support
    this.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        const focusedCard = e.target.closest(".splide__slide");
        if (focusedCard) {
          const link = focusedCard.querySelector("a");
          if (link) {
            e.preventDefault();
            link.click();
          }
        }
      }
    });

    // Ensure slides are focusable
    const slides = this.querySelectorAll(".splide__slide");
    slides.forEach((slide, index) => {
      slide.setAttribute("tabindex", "0");
      slide.setAttribute("role", "group");
      slide.setAttribute("aria-label", `Product ${index + 1}`);
    });
  }

  handleGeneralCardClick(card) {
    // Analytics tracking for general cards
    console.log("General card clicked:", card);

    // You can add analytics tracking here
    if (window.gtag) {
      gtag("event", "click", {
        event_category: "Product Catalog",
        event_label: "General Card",
      });
    }
  }

  handleProductCardClick(card) {
    // Analytics tracking for product cards
    console.log("Product card clicked:", card);

    // You can add analytics tracking here
    if (window.gtag) {
      gtag("event", "click", {
        event_category: "Product Catalog",
        event_label: "Product Card",
      });
    }
  }

  // Method to manually refresh the slider if needed
  refreshSlider() {
    this.splides.forEach((splide) => {
      if (splide) {
        splide.refresh();
      }
    });
  }

  // Method to go to a specific slide
  goToSlide(index) {
    const currentSplide = this.splides.get(this.currentTab || "default");
    if (currentSplide) {
      currentSplide.go(index);
    }
  }

  // Method to get current slide index
  getCurrentSlide() {
    const currentSplide = this.splides.get(this.currentTab || "default");
    return currentSplide ? currentSplide.index : 0;
  }

  // Method to get total number of slides
  getTotalSlides() {
    const currentSplide = this.splides.get(this.currentTab || "default");
    return currentSplide ? currentSplide.length : 0;
  }
}

// Register the custom element
customElements.define("cos-product-catalog-section", CosProductCatalogSection);

// Export for potential use in other scripts
window.CosProductCatalogSection = CosProductCatalogSection;
